import React, { useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle2,
  Upload as UploadIcon,
  X as XIcon,
  LayoutGrid,
  Globe2,
  Target,
  Handshake,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Honeypot from "@/components/Honeypot";
import { siteConfig } from "@/config/siteConfig";
import { COUNTRIES } from "@/data/countries";
import { submitLead, readAttribution, baseMeta, uploadFile } from "@/lib/api";
import { trackLeadSubmit } from "@/lib/analytics";
import TurnstileWidget, { useSubmitBlockedByTurnstile } from "@/components/TurnstileWidget";

const EMP = ["1–10", "11–50", "51–200", "201–1000", "1000+"];
const SALES = ["1–5", "6–20", "21–50", "51+"];
const TECH_EMP = ["0", "1–5", "6–20", "21–50", "51+"];
const PHARMA_CUST = ["0", "1–10", "11–50", "51–200", "200+"];

const STEPS = [
  { key: "company", num: 1, label: "Company" },
  { key: "market", num: 2, label: "Market" },
  { key: "capability", num: 3, label: "Capability" },
  { key: "partnership", num: 4, label: "Partnership" },
];

const WHY_PARTNER = [
  {
    key: "specialist",
    Icon: LayoutGrid,
    title: "Specialist Portfolio",
    body:
      "Access a focused range of printing and coding products for pharmaceutical manufacturing applications.",
  },
  {
    key: "international",
    Icon: Globe2,
    title: "Growing International Opportunity",
    body:
      "Work with a Dubai-based international business developing markets across the Middle East, South Asia and Africa.",
  },
  {
    key: "technical",
    Icon: Target,
    title: "Technical Sales Approach",
    body:
      "Build opportunities through application assessment, samples and product trials rather than price-only selling.",
  },
  {
    key: "long_term",
    Icon: Handshake,
    title: "Long-Term Partnership",
    body:
      "We are looking for partners interested in developing sustainable local markets and customer relationships.",
  },
];

const initial = {
  // step 1
  company: "",
  country: "",
  website: "",
  yearEstablished: "",
  employees: "",
  // step 2
  territories: "",
  industries: "",
  countriesCovered: "",
  pharmaCustomers: "",
  // step 3
  salesTeamSize: "",
  technicalEmployees: "",
  technicalService: "",
  warehousing: "",
  importCapability: "",
  codingBrands: "",
  packagingBrands: "",
  otherBrands: "",
  // step 4
  contactName: "",
  position: "",
  email: "",
  phone: "",
  pharmaCustomerBase: "",
  whyInterested: "",
};

const Label = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5">
    {children} {required && <span className="text-[var(--pharma-blue)]">*</span>}
  </label>
);
const inputCls =
  "w-full min-h-[44px] border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";

const FileField = ({ id, label, file, onChange, testid, help }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-3">
      <label
        htmlFor={id}
        className="inline-flex items-center gap-2 border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[13px] text-[var(--charcoal)] bg-white cursor-pointer hover:bg-[var(--bg-alt)] min-h-[44px]"
      >
        <UploadIcon className="w-4 h-4" strokeWidth={1.5} />
        Choose file
      </label>
      <input
        id={id}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testid}
      />
      {file && (
        <span className="text-[12.5px] text-[var(--slate)] inline-flex items-center gap-2 max-w-[240px] truncate">
          <span className="truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove file"
            className="text-[var(--slate)] hover:text-[var(--navy)]"
            data-testid={`${testid}-remove`}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </div>
    <p className="mt-1.5 text-[11.5px] text-[var(--slate)]">
      {help || "PDF preferred, or JPG / PNG / WEBP / GIF, up to 10 MB."}
    </p>
  </div>
);

const WizardSteps = ({ current }) => (
  <ol className="wizard-steps" data-testid="dist-wizard-steps" aria-label="Distributor application steps">
    {STEPS.map((s) => {
      const state = s.num < current ? "done" : s.num === current ? "active" : "todo";
      return (
        <li key={s.key} className="wizard-step" data-state={state} data-testid={`dist-step-indicator-${s.num}`}>
          <span className="wizard-step-num">{s.num}</span>
          <span className="wizard-step-label font-heading font-semibold text-[13.5px]">{s.label}</span>
        </li>
      );
    })}
  </ol>
);

const Distributors = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [hp, setHp] = useState("");
  const [profileFile, setProfileFile] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
  const inflightRef = useRef(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const attribution = useMemo(() => readAttribution(params), [params]);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const step1Valid = form.company.trim() && form.country.trim() && form.website.trim();
  const step2Valid =
    form.territories.trim() &&
    form.industries.trim() &&
    form.pharmaCustomers.trim();
  const step3Valid = form.technicalService && form.warehousing && form.importCapability;
  const step4Valid =
    form.contactName.trim() &&
    form.position.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.pharmaCustomerBase.trim() &&
    form.whyInterested.trim();

  const goNext = () => {
    setError(null);
    if (step === 1 && !step1Valid) return setError("Please complete the required fields marked with an asterisk.");
    if (step === 2 && !step2Valid) return setError("Please complete the required fields marked with an asterisk.");
    if (step === 3 && !step3Valid) return setError("Please answer the Technical Service, Warehousing and Import Capability questions.");
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const scrollToForm = () => {
    const el = document.getElementById("dist-form-anchor");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflightRef.current) return;
    if (!step4Valid) return setError("Please complete the required fields on this step.");
    inflightRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      let uploadedProfileId = null;
      if (profileFile) {
        const up = await uploadFile(profileFile);
        if (!up.ok) throw new Error(up.error || "Company profile upload failed");
        uploadedProfileId = up.data.id;
      }
      const res = await submitLead({
        type: "DISTRIBUTOR",
        source_page: "/distributors",
        contact: {
          name: form.contactName,
          company: form.company,
          position: form.position || null,
          email: form.email,
          phone: form.phone || null,
          country: form.country,
        },
        attribution,
        meta: baseMeta(),
        payload: {
          website: form.website || null,
          year_established: form.yearEstablished || null,
          employees: form.employees || null,
          sales_team_size: form.salesTeamSize || null,
          technical_employees: form.technicalEmployees || null,
          territories_covered: form.territories || null,
          industries_served: form.industries || null,
          pharma_customers_approx: form.pharmaCustomers || null,
          pharma_customer_base: form.pharmaCustomerBase,
          coding_printing_brands: form.codingBrands || null,
          other_brands:
            [form.packagingBrands, form.otherBrands].filter(Boolean).join(" | ") || null,
          technical_service: form.technicalService === "yes",
          warehousing: form.warehousing === "yes",
          import_capability: form.importCapability === "yes",
          countries_covered: form.countriesCovered || null,
          why_interested: form.whyInterested,
          uploaded_company_profile_id: uploadedProfileId,
        },
        _hp: hp,
        turnstile_token: captchaToken,
      });
      if (res.ok) {
        trackLeadSubmit("distributor");
        setResult(res.data);
      }
      else if (res.status === 403) setError("Verification failed. Please retry the challenge.");
      else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
      else if (res.status === 422) setError("Please check the required fields and try again.");
      else setError("Something went wrong. Please try again shortly.");
    } catch (err) {
      setError(err.message || "Upload or submission failed. Please try again.");
    } finally {
      setSubmitting(false);
      inflightRef.current = false;
    }
  };

  return (
    <>
      <SEO
        rawTitle={`Grow Your Pharmaceutical Manufacturing Portfolio | ${siteConfig.COMPANY_NAME}`}
        description="Apply to become a distribution partner for specialised pharmaceutical printing, coding and production solutions."
        path="/distributors"
      />
      <Helmet />

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Become a Distributor" }]} />
      </div>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-8" data-testid="dist-hero">
        <p className="eyebrow">Partnership</p>
        <h1 className="font-heading mt-4 text-[32px] leading-tight lg:text-[48px] lg:leading-[1.05] font-semibold text-[var(--navy)]" data-testid="dist-h1">
          Grow Your Pharmaceutical Manufacturing Portfolio
        </h1>
        <p className="mt-6 text-[16.5px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          Partner with us to introduce specialised pharmaceutical printing, coding and production solutions to your market.
        </p>
        <div className="mt-8">
          <button type="button" onClick={scrollToForm} className="btn-primary text-sm px-5 py-2.5" data-testid="dist-apply-cta">
            Apply to Become a Distributor
          </button>
        </div>
      </section>

      {/* Why Partner With Us (§11) */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 border-t border-[var(--border)]" data-testid="dist-why-partner">
        <p className="eyebrow">Why Partner With Us?</p>
        <h2 className="font-heading mt-4 text-2xl lg:text-[32px] font-semibold text-[var(--navy)]">
          Why Partner With Us?
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {WHY_PARTNER.map(({ key, Icon, title, body }) => (
            <div
              key={key}
              className="border border-[var(--border)] rounded-[4px] bg-white p-8"
              data-testid={`dist-why-${key}`}
            >
              <Icon className="w-9 h-9 text-[var(--pharma-blue)]" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading mt-5 text-[20px] font-semibold text-[var(--navy)]">{title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Wizard */}
      <section id="dist-form-anchor" className="mx-auto max-w-[1240px] px-6 lg:px-10 py-10 pb-24">
        {result ? (
          <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-10 bg-[var(--bg-alt)]" data-testid="dist-success">
            <CheckCircle2 strokeWidth={1.5} style={{ width: 44, height: 44, color: "var(--pharma-blue)" }} aria-hidden="true" />
            <h2 className="font-heading mt-4 text-2xl font-semibold text-[var(--navy)]">
              Thank you. Your application details have been received and will be reviewed by our team.
            </h2>
            <p className="mt-6 text-[12px] text-[var(--slate)]" data-testid="dist-reference-id">
              Application ID: {result.id}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="max-w-[820px]" data-testid="dist-form" noValidate>
            <WizardSteps current={step} />
            <div className="mt-6 border border-[var(--border)] rounded-[4px] p-6 lg:p-8 bg-white">
              {error && (
                <div className="mb-6 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="dist-error">
                  {error}
                </div>
              )}

              {/* STEP 1 */}
              {step === 1 && (
                <div data-testid="dist-step-1">
                  <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Company</h2>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="d-company" required>Company Name</Label>
                      <input id="d-company" name="company" required className={inputCls} value={form.company} onChange={onChange} data-testid="d-company" />
                    </div>
                    <div>
                      <Label htmlFor="d-country" required>Country</Label>
                      <input list="d-country-list" id="d-country" name="country" required className={inputCls} value={form.country} onChange={onChange} data-testid="d-country" placeholder="Type to search…" />
                      <datalist id="d-country-list">
                        {COUNTRIES.map(([code, name]) => (<option key={code} value={name} />))}
                      </datalist>
                    </div>
                    <div>
                      <Label htmlFor="d-website" required>Website</Label>
                      <input id="d-website" name="website" required className={inputCls} value={form.website} onChange={onChange} data-testid="d-website" />
                    </div>
                    <div>
                      <Label htmlFor="d-year">Year Established</Label>
                      <input id="d-year" name="yearEstablished" className={inputCls} value={form.yearEstablished} onChange={onChange} data-testid="d-year" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-employees">Number of Employees</Label>
                      <select id="d-employees" name="employees" className={inputCls} value={form.employees} onChange={onChange} data-testid="d-employees">
                        <option value="">Select…</option>
                        {EMP.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div data-testid="dist-step-2">
                  <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Market Coverage</h2>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-territories" required>Territories Covered</Label>
                      <textarea id="d-territories" name="territories" required rows={2} className={inputCls} value={form.territories} onChange={onChange} data-testid="d-territories" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-industries" required>Industries Served</Label>
                      <textarea id="d-industries" name="industries" required rows={2} className={inputCls} value={form.industries} onChange={onChange} data-testid="d-industries" />
                    </div>
                    <div>
                      <Label htmlFor="d-countriesCovered">Countries Covered</Label>
                      <input id="d-countriesCovered" name="countriesCovered" className={inputCls} value={form.countriesCovered} onChange={onChange} data-testid="d-countriesCovered" />
                    </div>
                    <div>
                      <Label htmlFor="d-pharmaCustomers" required>Approx. Pharmaceutical Customers</Label>
                      <select id="d-pharmaCustomers" name="pharmaCustomers" required className={inputCls} value={form.pharmaCustomers} onChange={onChange} data-testid="d-pharmaCustomers">
                        <option value="">Select…</option>
                        {PHARMA_CUST.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <div data-testid="dist-step-3">
                  <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Capabilities</h2>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="d-salesTeamSize">Sales Team Size</Label>
                      <select id="d-salesTeamSize" name="salesTeamSize" className={inputCls} value={form.salesTeamSize} onChange={onChange} data-testid="d-salesTeamSize">
                        <option value="">Select…</option>
                        {SALES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="d-technicalEmployees">Number of Technical / Service Employees</Label>
                      <select id="d-technicalEmployees" name="technicalEmployees" className={inputCls} value={form.technicalEmployees} onChange={onChange} data-testid="d-technicalEmployees">
                        <option value="">Select…</option>
                        {TECH_EMP.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label required>Technical Service Capability</Label>
                      <div className="flex gap-6 mt-1">
                        {["yes", "no"].map((v) => (
                          <label key={v} className="inline-flex items-center gap-2 text-[14px]">
                            <input type="radio" name="technicalService" value={v} checked={form.technicalService === v} onChange={onChange} data-testid={`d-technicalService-${v}`} />
                            <span className="capitalize">{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label required>Warehouse</Label>
                      <div className="flex gap-6 mt-1">
                        {["yes", "no"].map((v) => (
                          <label key={v} className="inline-flex items-center gap-2 text-[14px]">
                            <input type="radio" name="warehousing" value={v} checked={form.warehousing === v} onChange={onChange} data-testid={`d-warehousing-${v}`} />
                            <span className="capitalize">{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label required>Import Capability</Label>
                      <div className="flex gap-6 mt-1">
                        {["yes", "no"].map((v) => (
                          <label key={v} className="inline-flex items-center gap-2 text-[14px]">
                            <input type="radio" name="importCapability" value={v} checked={form.importCapability === v} onChange={onChange} data-testid={`d-importCapability-${v}`} />
                            <span className="capitalize">{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-codingBrands">Coding / Printing Brands Represented</Label>
                      <textarea id="d-codingBrands" name="codingBrands" rows={2} className={inputCls} value={form.codingBrands} onChange={onChange} data-testid="d-codingBrands" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-packagingBrands">Packaging Brands Represented</Label>
                      <textarea id="d-packagingBrands" name="packagingBrands" rows={2} className={inputCls} value={form.packagingBrands} onChange={onChange} data-testid="d-packagingBrands" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-otherBrands">Other International Brands Represented</Label>
                      <textarea id="d-otherBrands" name="otherBrands" rows={2} className={inputCls} value={form.otherBrands} onChange={onChange} data-testid="d-otherBrands" />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4 */}
              {step === 4 && (
                <div data-testid="dist-step-4">
                  <h2 className="font-heading text-[20px] font-semibold text-[var(--navy)]">Partnership</h2>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="d-contactName" required>Contact Name</Label>
                      <input id="d-contactName" name="contactName" required className={inputCls} value={form.contactName} onChange={onChange} data-testid="d-contactName" />
                    </div>
                    <div>
                      <Label htmlFor="d-position" required>Position</Label>
                      <input id="d-position" name="position" required className={inputCls} value={form.position} onChange={onChange} data-testid="d-position" />
                    </div>
                    <div>
                      <Label htmlFor="d-email" required>Email</Label>
                      <input id="d-email" name="email" type="email" required className={inputCls} value={form.email} onChange={onChange} data-testid="d-email" />
                    </div>
                    <div>
                      <Label htmlFor="d-phone" required>Mobile / WhatsApp</Label>
                      <input id="d-phone" name="phone" required className={inputCls} value={form.phone} onChange={onChange} data-testid="d-phone" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-pharmaCustomerBase" required>Tell Us About Your Pharmaceutical Customer Base</Label>
                      <textarea id="d-pharmaCustomerBase" name="pharmaCustomerBase" rows={4} required className={inputCls} value={form.pharmaCustomerBase} onChange={onChange} data-testid="d-pharmaCustomerBase" placeholder="Type of customers, size, geography, applications served…" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="d-whyInterested" required>Why Are You Interested in Our Products?</Label>
                      <textarea id="d-whyInterested" name="whyInterested" rows={5} required className={inputCls} value={form.whyInterested} onChange={onChange} data-testid="d-whyInterested" />
                    </div>
                    <div className="sm:col-span-2">
                      <FileField id="d-profile-upload" label="Upload Company Profile" file={profileFile} onChange={setProfileFile} testid="d-profile-upload" />
                    </div>
                  </div>
                  <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
                  <div
                    className="mt-8 border border-[var(--border)] rounded-[4px] p-4 bg-[var(--bg-alt)] text-[13.5px] italic text-[var(--slate)]"
                    data-testid="dist-exclusivity-disclaimer"
                  >
                    Distribution arrangements, territories and any exclusivity are subject
                    to commercial evaluation and agreed performance requirements.
                  </div>
                </div>
              )}

              {/* Nav buttons */}
              <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
                {step > 1 ? (
                  <button type="button" onClick={goBack} className="btn-outline text-sm inline-flex items-center gap-2" data-testid="dist-back">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                ) : (
                  <span />
                )}
                {step < 4 ? (
                  <button type="button" onClick={goNext} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="dist-next">
                    {step === 1 && "Next — Market Coverage"}
                    {step === 2 && "Next — Capabilities"}
                    {step === 3 && "Next — Partnership"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting || captchaBlocks} className="btn-primary text-sm disabled:opacity-70" data-testid="dist-submit">
                    {submitting ? "Submitting…" : "Submit Distributor Application"}
                  </button>
                )}
              </div>
              {step === 4 && (
                <div className="pt-2">
                  <TurnstileWidget onToken={setCaptchaToken} action="distributor" testid="dist-turnstile" />
                </div>
              )}
            </div>
          </form>
        )}
      </section>
    </>
  );
};

export default Distributors;
